import { useEffect, useState, useContext } from "react";
import {
  Card,
  Grid,
  IconButton,
  TextField,
  MenuItem,
  Snackbar,
  Alert,
  CircularProgress,
  Tooltip,
  Box,
  Paper,
  Chip,
  Divider,
} from "@mui/material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import Icon from "@mui/material/Icon";
import axiosInstance from "api/axiosInstance";
import { useTheme } from "@mui/material/styles";
import { LanguageContext } from "../../../../context/LanguageContext";

export default function APIKeyManager() {
  const [keys, setKeys] = useState([]);
  const [newName, setNewName] = useState("");
  const [newType, setNewType] = useState("LIB");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [editKeyId, setEditKeyId] = useState(null);
  const theme = useTheme();
  const { componentLanguageData, loadLanguageFile, language } = useContext(LanguageContext);
  useEffect(() => {
    loadLanguageFile("APIKeyManager", "apiToken/APIKeyManager/APIKeyManager_lang");
  }, [language, loadLanguageFile]);
  const LANGUAGE = componentLanguageData.APIKeyManager || { Main: {} };

  const loadKeys = async () => {
    try {
      setLoading(true);
      const res = await axiosInstance.get("/users/api-keys");
      const data = Array.isArray(res.data) ? res.data : [];
      setKeys(data);
    } catch (err) {
      console.error("載入失敗", err);
      setError(LANGUAGE.Main.LoadFailed || "載入失敗，請稍後再試");
    } finally {
      setLoading(false);
    }
  };

  const createKey = async () => {
    if (!newName.trim()) {
      setError(LANGUAGE.Main.NeedName || "請輸入 Token 名稱");
      return;
    }
    try {
      setLoading(true);
      await axiosInstance.post("/users/api-keys", { name: newName, type: newType });
      setNewName("");
      setSuccess(LANGUAGE.Main.CreateSuccess || "Token 已成功建立");
      await loadKeys();
    } catch (err) {
      console.error("建立失敗", err);
      setError(LANGUAGE.Main.CreateFailed || "建立失敗");
    } finally {
      setLoading(false);
    }
  };

  const deleteKey = async (id) => {
    if (!window.confirm(LANGUAGE.Main.ConfirmDelete || "確定要刪除此 Token 嗎？此動作無法復原。")) return;
    try {
      setLoading(true);
      await axiosInstance.delete(`/users/api-keys/${id}`);
      setSuccess(LANGUAGE.Main.DeleteSuccess || "Token 已刪除");
      await loadKeys();
    } catch (err) {
      console.error("刪除失敗", err);
      setError(LANGUAGE.Main.DeleteFailed || "刪除失敗");
    } finally {
      setLoading(false);
    }
  };

  const updateKey = async (id, name, type) => {
    try {
      await axiosInstance.put(`/users/api-keys/${id}`, { name, type });
      setSuccess(LANGUAGE.Main.UpdateSuccess || "更新成功");
      setEditKeyId(null);
      await loadKeys();
    } catch (err) {
      console.error("更新失敗", err);
      setError(LANGUAGE.Main.UpdateFailed || "更新失敗");
    }
  };

  const handleCopy = async (token) => {
    try {
      await navigator.clipboard.writeText(token);
      setSuccess(LANGUAGE.Main.CopySuccess || "Token 已複製到剪貼簿！");
    } catch (err) {
      setError(LANGUAGE.Main.CopyFailed || "無法複製 Token");
    }
  };

  useEffect(() => {
    loadKeys();
  }, []);

  const getTypeColor = (type) => {
    switch (type) {
      case "LIB": return "info";
      case "TOOL": return "warning";
      case "API": return "success";
      default: return "default";
    }
  };

  return (
    <MDBox pt={6} pb={3}>
      <Grid container spacing={6} justifyContent="center">
        <Grid item xs={12} lg={10}>
          <Card sx={{ overflow: "visible" }}>
            <MDBox
              mx={2}
              mt={-3}
              py={3}
              px={2}
              variant="gradient"
              bgColor="info"
              borderRadius="lg"
              coloredShadow="info"
              display="flex"
              justifyContent="space-between"
              alignItems="center"
            >
              <MDBox>
                <MDTypography variant="h5" color="white">
                  API Access Tokens
                </MDTypography>
                <MDTypography variant="button" color="white" opacity={0.8}>
                  {LANGUAGE.Main.Subtitle || "管理您的 API 金鑰以存取平台資源"}
                </MDTypography>
              </MDBox>
              <Icon fontSize="medium" sx={{ color: "white" }}>vpn_key</Icon>
            </MDBox>

            <MDBox p={3}>
              {/* Creation Section */}
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  mb: 4,
                  backgroundColor: theme.palette.grey[100],
                  borderRadius: 2,
                  border: `1px solid ${theme.palette.grey[300]}`,
                }}
              >
                <Grid container spacing={2} alignItems="flex-end">
                  <Grid item xs={12} md={5}>
                    <MDTypography variant="caption" fontWeight="bold" color="text" mb={1} display="block">
                      {LANGUAGE.Main.FieldName || "TOKEN 名稱"}
                    </MDTypography>
                    <TextField
                      placeholder={LANGUAGE.Main.FieldNamePlaceholder || "例如：我的交易機器人"}
                      fullWidth
                      variant="outlined"
                      size="small"
                      value={newName}
                      onChange={(e) => setNewName(e.target.value)}
                      sx={{ backgroundColor: "white" }}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <MDTypography variant="caption" fontWeight="bold" color="text" mb={1} display="block">
                      {LANGUAGE.Main.FieldType || "用途類型"}
                    </MDTypography>
                    <TextField
                      select
                      fullWidth
                      variant="outlined"
                      size="small"
                      value={newType}
                      onChange={(e) => setNewType(e.target.value)}
                      sx={{ backgroundColor: "white" }}
                    >
                      <MenuItem value="LIB">{LANGUAGE.Main.TypeLib || "LIB (函式庫)"}</MenuItem>
                      <MenuItem value="TOOL">{LANGUAGE.Main.TypeTool || "TOOL (工具)"}</MenuItem>
                      <MenuItem value="API">{LANGUAGE.Main.TypeApi || "API (介面)"}</MenuItem>
                      <MenuItem value="OTHER">{LANGUAGE.Main.TypeOther || "OTHER (其他)"}</MenuItem>
                    </TextField>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <MDButton
                      variant="gradient"
                      color="dark"
                      fullWidth
                      onClick={createKey}
                      disabled={loading}
                      startIcon={<Icon>add</Icon>}
                    >
                      {LANGUAGE.Main.BtnCreate || "產生新 Token"}
                    </MDButton>
                  </Grid>
                </Grid>
              </Paper>

              <Divider sx={{ my: 3 }} />

              <MDTypography variant="h6" gutterBottom>
                {LANGUAGE.Main.SectionExisting || "現有的 Tokens"}
              </MDTypography>

              {/* Token List */}
              {loading && keys.length === 0 ? (
                <MDBox display="flex" justifyContent="center" py={5}>
                  <CircularProgress color="info" />
                </MDBox>
              ) : keys.length > 0 ? (
                <MDBox display="flex" flexDirection="column" gap={2}>
                  {keys.map((key) => (
                    <Paper
                      key={key.id}
                      elevation={0}
                      sx={{
                        p: 2,
                        border: `1px solid ${theme.palette.divider}`,
                        borderRadius: 2,
                        transition: "all 0.2s",
                        "&:hover": {
                          borderColor: theme.palette.info.main,
                          boxShadow: theme.shadows[2],
                          transform: "translateY(-2px)",
                        },
                      }}
                    >
                      <Grid container alignItems="center" spacing={2}>
                        <Grid item xs={12} sm={3}>
                          {editKeyId === key.id ? (
                            <TextField
                              fullWidth
                              size="small"
                              value={key.name}
                              onChange={(e) =>
                                setKeys((prev) =>
                                  prev.map((k) => (k.id === key.id ? { ...k, name: e.target.value } : k))
                                )
                              }
                            />
                          ) : (
                            <Box display="flex" alignItems="center" gap={1}>
                              <Icon color="inherit" fontSize="small" sx={{ opacity: 0.5 }}>label</Icon>
                              <MDTypography variant="subtitle2" fontWeight="medium">
                                {key.name}
                              </MDTypography>
                            </Box>
                          )}
                        </Grid>

                        <Grid item xs={12} sm={2}>
                          {editKeyId === key.id ? (
                            <TextField
                              select
                              fullWidth
                              size="small"
                              value={key.type}
                              onChange={(e) =>
                                setKeys((prev) =>
                                  prev.map((k) => (k.id === key.id ? { ...k, type: e.target.value } : k))
                                )
                              }
                            >
                              <MenuItem value="LIB">LIB</MenuItem>
                              <MenuItem value="TOOL">TOOL</MenuItem>
                              <MenuItem value="API">API</MenuItem>
                              <MenuItem value="OTHER">OTHER</MenuItem>
                            </TextField>
                          ) : (
                            <Chip
                              label={key.type}
                              color={getTypeColor(key.type)}
                              variant="outlined"
                              size="small"
                              sx={{ fontWeight: "bold", borderRadius: "4px" }}
                            />
                          )}
                        </Grid>

                        <Grid item xs={12} sm={4}>
                          <Paper
                            variant="outlined"
                            sx={{
                              p: 1,
                              px: 2,
                              backgroundColor: theme.palette.grey[50],
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              fontFamily: "monospace",
                              fontSize: "0.85rem",
                              color: theme.palette.text.secondary,
                            }}
                          >
                            {key.apiKey ? `${key.apiKey.slice(0, 8)}••••••••${key.apiKey.slice(-6)}` : "••••••••••••••••"}
                            <Tooltip title={LANGUAGE.Main.TipCopy || "複製 Token"}>
                              <IconButton
                                size="small"
                                onClick={() => handleCopy(key.apiKey)}
                                sx={{ ml: 1 }}
                              >
                                <Icon fontSize="small">content_copy</Icon>
                              </IconButton>
                            </Tooltip>
                          </Paper>
                        </Grid>

                        <Grid item xs={12} sm={3} display="flex" justifyContent="flex-end" gap={1}>
                          {editKeyId === key.id ? (
                            <Tooltip title={LANGUAGE.Main.TipSave || "儲存"}>
                              <IconButton color="success" onClick={() => updateKey(key.id, key.name, key.type)}>
                                <Icon>check_circle</Icon>
                              </IconButton>
                            </Tooltip>
                          ) : (
                            <Tooltip title={LANGUAGE.Main.TipEdit || "編輯"}>
                              <IconButton size="small" onClick={() => setEditKeyId(key.id)}>
                                <Icon fontSize="small">edit</Icon>
                              </IconButton>
                            </Tooltip>
                          )}
                          <Tooltip title={LANGUAGE.Main.TipDelete || "刪除"}>
                            <IconButton color="error" size="small" onClick={() => deleteKey(key.id)}>
                              <Icon fontSize="small">delete_outline</Icon>
                            </IconButton>
                          </Tooltip>
                        </Grid>
                      </Grid>
                    </Paper>
                  ))}
                </MDBox>
              ) : (
                <MDBox
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                  justifyContent="center"
                  py={6}
                  sx={{
                    border: `2px dashed ${theme.palette.divider}`,
                    borderRadius: 2,
                    backgroundColor: theme.palette.grey[50],
                  }}
                >
                  <Icon fontSize="large" color="disabled" sx={{ mb: 2, fontSize: "3rem !important" }}>
                    vpn_key_off
                  </Icon>
                  <MDTypography variant="h6" color="text" gutterBottom>
                    {LANGUAGE.Main.EmptyTitle || "尚無 API Tokens"}
                  </MDTypography>
                  <MDTypography variant="body2" color="text" opacity={0.6}>
                    {LANGUAGE.Main.EmptyDesc || "請使用上方的表單建立您的第一個 API Token"}
                  </MDTypography>
                </MDBox>
              )}
            </MDBox>
          </Card>
        </Grid>
      </Grid>

      <Snackbar
        open={!!success}
        autoHideDuration={3000}
        onClose={() => setSuccess("")}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert severity="success" variant="filled" onClose={() => setSuccess("")}>
          {success}
        </Alert>
      </Snackbar>
      <Snackbar
        open={!!error}
        autoHideDuration={3000}
        onClose={() => setError("")}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert severity="error" variant="filled" onClose={() => setError("")}>
          {error}
        </Alert>
      </Snackbar>
    </MDBox>
  );
}
